import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import {
  Search,
  Eye,
  Trash2,
  Home,
  Building2,
  Wrench,
} from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { QuoteDetailDialog } from "./QuoteDetailDialog";

const statusColors = {
  new: "bg-yellow-100 text-yellow-800",
  contacted: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  quoted: "bg-indigo-100 text-indigo-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-red-100 text-red-800",
};

const projectTypeIcons = {
  residential: Home,
  commercial: Building2,
  renovation: Wrench,
};

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const fetchQuotes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter !== "all") params.append("project_type", typeFilter);
      const response = await adminApi.get(`/admin/quotes?${params.toString()}`);
      setQuotes(response.data.quotes);
      setTotal(response.data.total);
    } catch {
      toast.error("Failed to fetch quotes");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleStatusChange = useCallback(async (quoteId, newStatus) => {
    try {
      await adminApi.patch(`/admin/quotes/${quoteId}/status?status=${newStatus}`);
      toast.success("Status updated successfully");
      fetchQuotes();
      setSelectedQuote((prev) => prev?.id === quoteId ? { ...prev, status: newStatus } : prev);
    } catch {
      toast.error("Failed to update status");
    }
  }, [fetchQuotes]);

  const handleDelete = useCallback(async (quoteId) => {
    if (!confirm("Are you sure you want to delete this quote?")) return;
    try {
      await adminApi.delete(`/admin/quotes/${quoteId}`);
      toast.success("Quote deleted successfully");
      fetchQuotes();
      setIsDetailOpen(false);
    } catch {
      toast.error("Failed to delete quote");
    }
  }, [fetchQuotes]);

  const filteredQuotes = useMemo(() =>
    quotes.filter(
      (q) =>
        q.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.phone?.includes(searchTerm)
    ), [quotes, searchTerm]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="admin-quotes" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900 uppercase">Quote Requests</h1>
          <p className="text-gray-600 mt-1">{total} total requests</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-white border-0 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search by name, email, phone..."
              data-testid="search-quotes"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter" className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="won">Won</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="type-filter" className="w-40"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="residential">Residential</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="renovation">Renovation</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        {filteredQuotes.length === 0 ? (
          <p className="text-gray-500 text-center py-12">No quotes found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">Name</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">Type</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">Budget</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">Date</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredQuotes.map((quote) => {
                  const TypeIcon = projectTypeIcons[quote.project_type] || Home;
                  return (
                    <tr key={quote.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{quote.full_name}</p>
                          <p className="text-sm text-gray-500">{quote.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline" className="capitalize">
                          <TypeIcon className="w-3 h-3 mr-1" />{quote.project_type}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{quote.budget_range || "-"}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[quote.status] || statusColors.new}`}>
                          {quote.status || "new"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`view-quote-${quote.id}`}
                            onClick={() => { setSelectedQuote(quote); setIsDetailOpen(true); }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 hover:bg-red-50"
                            onClick={() => handleDelete(quote.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <QuoteDetailDialog
        quote={selectedQuote}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
      />
    </div>
  );
};
